// Target of the home-screen shortcuts (manifest "shortcuts"): "#/hitro/opomnik", "#/hitro/sestanek"
// and "#/hitro/nadzor". Such a link can come from anywhere (an e-mail, a chat, another web site),
// so opening it never switches the microphone on and never creates a document: the page shows one
// big button that does it when tapped. (Recording with one tap from the home screen, without
// opening the app: the Android app's widgets.)
import { useState } from 'react';
import { createDocument } from '../lib/api';
import { defaultTitle } from '../lib/format';
import { navigate, type QuickMode } from '../lib/router';
import { errorText, newId, routeForDocument } from '../components/helpers';
import { Icon } from '../components/Icon';
import { Link } from '../components/NavGuard';
import { RecordButton } from '../components/RecordButton';
import { toast } from '../components/Toasts';
import { requestNoteAutoStart } from './Notes';

const QUICK: Record<QuickMode, { title: string; label: string; text: string }> = {
  note: {
    title: 'Nov opomnik',
    label: 'Začni snemanje opomnika',
    text: 'Snemanje se začne, ko tapnete gumb. Ko poveste opomnik, ga ustavite in shranite.',
  },
  meeting: {
    title: 'Nov sestanek',
    label: 'Začni sestanek',
    text: 'Ustvari nov sestanek. Snemanje začnete na njegovi strani.',
  },
  inspection: {
    title: 'Nov nadzor',
    label: 'Začni nadzor',
    text: 'Ustvari nov zapisnik nadzora, v katerega dodajate glasovne in pisne vnose.',
  },
};

export function QuickStart({ mode }: { mode: QuickMode }) {
  const [busy, setBusy] = useState(false);
  const quick = QUICK[mode];

  /** Runs only on the user's tap. */
  const start = async () => {
    if (busy) return;
    if (mode === 'note') {
      // The notes page starts recording as soon as it is shown (still within this tap).
      requestNoteAutoStart();
      navigate({ name: 'notes' }, { replace: true });
      return;
    }
    setBusy(true);
    const id = newId();
    try {
      await createDocument(id, { type: mode, title: defaultTitle(mode) }, { isNew: true });
      navigate(routeForDocument({ id, type: mode }), { replace: true });
    } catch (err) {
      toast(errorText(err, 'Dokumenta ni bilo mogoče ustvariti.'), { kind: 'error' });
      setBusy(false);
    }
  };

  return (
    <div className="page page--center page--quick">
      <section className="card quick-start" aria-labelledby="quick-title">
        <h1 id="quick-title" className="page-title">
          {quick.title}
        </h1>
        {mode === 'note' ? (
          <RecordButton recording={false} label={quick.label} onClick={() => void start()} />
        ) : (
          <button type="button" className={`action-card action-card--${mode}`} onClick={() => void start()} disabled={busy}>
            <span className="action-card__icon">
              <Icon name={busy ? 'spinner' : mode} size={30} />
            </span>
            <span className="action-card__text">
              <span className="action-card__title">{quick.label}</span>
            </span>
          </button>
        )}
        <p className="muted">{quick.text}</p>
        <Link to={{ name: 'home' }} className="btn btn--ghost">
          <Icon name="chevronLeft" size={20} /> Na začetno stran
        </Link>
      </section>
    </div>
  );
}
